import { useCallback, useMemo } from "react";
import { useNavigate, useNavigationType, useParams } from "react-router";
import {
  EventIdPublic,
  UserIdPublic,
} from "@shared/schemas/branded-id.schemas";
import type {
  EventParticipantIdPublic,
  EventSlug,
} from "@shared/schemas/branded-id.schemas";
import { UserAppRoutes } from "@shared/constants/user-app-routes.constants";
import type {
  EventRouteContext,
  EventRoutes,
} from "@shared/constants/user-app-routes.constants";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import { useEventSubdomain } from "./useEventSubdomain";

export interface AppNavigation {
  readonly toDirectory: () => void;

  readonly toDirectoryTopPicks: () => void;

  readonly toDirectoryRequests: () => void;

  readonly toDirectoryConnected: () => void;

  readonly toDirectorySent: () => void;

  readonly toParticipantProfile: (id: EventParticipantIdPublic) => void;
  /** /d/:eventId/profile/:participantId?tab=why-connect */
  readonly toParticipantProfileWhyConnect: (
    id: EventParticipantIdPublic,
  ) => void;

  readonly toProfileEdit: () => void;

  readonly toProfileEditPreferences: () => void;

  readonly toProfilePhoto: () => void;

  readonly toUserAccount: (id: UserIdPublic) => void;

  readonly toUserAccountEvents: (id: UserIdPublic) => void;

  readonly back: (fallback: () => void) => void;
}

export function useAppNavigation(eventSlug?: EventSlug | null): AppNavigation {
  const navigate = useNavigate();
  const navType = useNavigationType();
  const subdomain = useEventSubdomain();
  const { eventIdPublicOrSlug: eventIdParam } = useParams<{
    eventIdPublicOrSlug?: string;
  }>();

  const isEventSubdomain = subdomain !== null;
  const rawEventId = eventIdParam ?? subdomain ?? undefined;

  const eventRoutes = useMemo((): EventRoutes | undefined => {
    if (rawEventId === undefined) return undefined;
    const ctx: EventRouteContext = {
      eventIdPublic: EventIdPublic.unsafeCreate(rawEventId),
      isEventSubdomain,
      ...(eventSlug != null ? { eventSlug } : {}),
    };
    return UserAppRoutes.forEvent(ctx);
  }, [rawEventId, isEventSubdomain, eventSlug]);

  const go = useCallback(
    (path: string) => {
      void navigate(path);
    },
    [navigate],
  );

  const goEvent = useCallback(
    (
      method: string,
      action: (routes: NonNullable<typeof eventRoutes>) => void,
    ) => {
      if (eventRoutes !== undefined) {
        action(eventRoutes);
      } else {
        applicationLogger.warn(
          `useAppNavigation.${method} called without event context — navigation suppressed`,
          "useAppNavigation",
        );
      }
    },
    [eventRoutes],
  );

  return useMemo(
    (): AppNavigation => ({
      toDirectory: (): void => {
        goEvent("toDirectory", (r) => {
          go(r.directory());
        });
      },
      toDirectoryTopPicks: (): void => {
        goEvent("toDirectoryTopPicks", (r) => {
          go(r.directoryTopPicks());
        });
      },
      toDirectoryRequests: (): void => {
        goEvent("toDirectoryRequests", (r) => {
          go(r.directoryRequests());
        });
      },
      toDirectoryConnected: (): void => {
        goEvent("toDirectoryConnected", (r) => {
          go(r.directoryConnected());
        });
      },
      toDirectorySent: (): void => {
        goEvent("toDirectorySent", (r) => {
          go(r.directorySent());
        });
      },
      toParticipantProfile: (id): void => {
        goEvent("toParticipantProfile", (r) => {
          go(r.participantProfile(id));
        });
      },
      toParticipantProfileWhyConnect: (id): void => {
        goEvent("toParticipantProfileWhyConnect", (r) => {
          go(r.participantProfileWhyConnect(id));
        });
      },
      toProfileEdit: (): void => {
        goEvent("toProfileEdit", (r) => {
          go(r.profileEdit());
        });
      },
      toProfileEditPreferences: (): void => {
        goEvent("toProfileEditPreferences", (r) => {
          go(r.profileEditPreferences());
        });
      },
      toProfilePhoto: (): void => {
        goEvent("toProfilePhoto", (r) => {
          go(r.profilePhoto());
        });
      },
      toUserAccount: (id): void => {
        go(UserAppRoutes.userAccount(id));
      },
      toUserAccountEvents: (id): void => {
        go(UserAppRoutes.userAccountEvents(id));
      },
      back: (fallback): void => {
        if (navType !== "POP") {
          void navigate(-1);
        } else {
          fallback();
        }
      },
    }),
    [go, goEvent, navigate, navType],
  );
}
